/**
 * Administración de categorías (puro): cuánto se usa cada una (para avisar antes de fusionar o eliminar), qué
 * nombres valen y cómo quedan los «compartidos al importar» al renombrar, fusionar o eliminar.
 */
import { DIVIDED, NO_CATEGORY, SAVINGS_GOAL } from '@pf/domain';
import { ADJUSTMENT_CATEGORY, type ArchivedMovement } from '@pf/workbook';
import type { Computed } from './model.js';
import type { AccountImportDefaults } from './import-model.js';

/** Categorías de la propia app: no se editan, renombran ni eliminan. */
export const SYSTEM_CATEGORIES = [DIVIDED, ADJUSTMENT_CATEGORY, NO_CATEGORY, SAVINGS_GOAL];
export const isSystemCategory = (name: string) => SYSTEM_CATEGORIES.some(s => s.toLowerCase() === name.trim().toLowerCase());

export interface CategoryUsage {
  movements: number; archived: number; parts: number; planned: number; budget: number; notes: number; rules: number;
  /** Este año, en positivo (gasto o ingreso atribuible) y cuántos movimientos. */
  yearTotal: number; yearCount: number;
}

export function categoryUsage(c: Computed, archive: ArchivedMovement[], name: string, year: number): CategoryUsage {
  const is = (n: string) => n.trim().toLowerCase() === name.trim().toLowerCase();
  const items = c.items.filter(i => is(i.category) && i.month.startsWith(`${year}-`));
  return {
    movements: c.wb.movements.filter(m => is(m.category)).length,
    archived: archive.filter(a => is(a.category)).length,
    parts: c.wb.parts.filter(p => is(p.category)).length,
    planned: (c.wb.planned ?? []).filter(p => is(p.category)).length,
    budget: c.wb.budget.filter(b => is(b.category)).length,
    notes: c.wb.notes.filter(n => is(n.category)).length,
    rules: c.wb.rules.filter(r => is(r.category)).length,
    yearTotal: Math.abs(Math.round(items.reduce((t, i) => t + i.attributable, 0) * 100) / 100),
    yearCount: c.movements.filter(m => is(m.effCategory) && m.effMonth.startsWith(`${year}-`)).length,
  };
}

/** Se puede eliminar si no la usa ningún movimiento, división ni gasto previsto (presupuesto, notas y reglas se van con ella). */
export const canDelete = (u: CategoryUsage) => !u.movements && !u.archived && !u.parts && !u.planned;

/** Problema con el nombre nuevo, o '' si vale. */
export function checkCategoryName(name: string): string {
  const n = name.trim();
  if (!n) return 'Escribe un nombre';
  if (n.length > 40) return 'Máximo 40 letras';
  if (/^[=+\-@]/.test(n)) return 'No puede empezar por = + - @';
  if (isSystemCategory(n)) return `«${n}» es un nombre que usa la app`;
  return '';
}

/** «Compartidos al importar» tras renombrar o fusionar (to) o eliminar (to = null) una categoría. */
export function renameInImportDefaults(list: AccountImportDefaults[], from: string, to: string | null): AccountImportDefaults[] {
  const is = (n: string) => n.trim().toLowerCase() === from.trim().toLowerCase();
  const swap = (names: string[]) => [...new Set(names.flatMap(n => (is(n) ? (to ? [to] : []) : [n])))];
  return list.map(x => { const tricount = swap(x.tricount); return { ...x, tricount, shared: swap(x.shared).filter(n => !tricount.includes(n)) }; })
    .filter(x => x.shared.length || x.tricount.length);
}
